import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Paperclip, Trash2, Upload, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { shortDate } from "@/lib/format";

export const AttachmentButton = ({ count, onClick, testid = "attachments-button" }) => (
  <button data-testid={testid} onClick={onClick} aria-label="Receipts & attachments"
    className={`relative ${count ? "text-emerald-600" : "text-slate-400"} hover:text-slate-900`}>
    <Paperclip size={15} />
    {count > 0 && <span className="absolute -right-2 -top-2 rounded-full bg-emerald-600 px-1 text-[10px] font-bold text-white">{count}</span>}
  </button>
);

const Preview = ({ att }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let active = true, blob;
    api.get(`/attachments/${att.id}/download`, { responseType: "blob" }).then(({ data }) => {
      blob = URL.createObjectURL(data);
      if (active) setUrl(blob);
    }).catch(() => {});
    return () => { active = false; if (blob) URL.revokeObjectURL(blob); };
  }, [att.id]);
  if (!url) return <div className="grid h-24 w-24 place-items-center rounded bg-slate-100 text-xs text-slate-400">Loading…</div>;
  if (att.content_type === "application/pdf") {
    return <a href={url} target="_blank" rel="noreferrer" data-testid="attachment-open-pdf-link"
      className="grid h-24 w-24 place-items-center rounded border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"><FileText size={28} /></a>;
  }
  return <a href={url} target="_blank" rel="noreferrer" data-testid="attachment-open-image-link">
    <img src={url} alt={att.original_filename} className="h-24 w-24 rounded border border-slate-200 object-cover" /></a>;
};

export const AttachmentsDialog = ({ entityType, entityId, title, onClose, onChanged }) => {
  const { canWrite } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/attachments", { params: { entity_type: entityType, entity_id: entityId } })
    .then(({ data }) => setItems(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityId]);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("entity_type", entityType); fd.append("entity_id", entityId); fd.append("file", file);
        await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} file(s) attached`);
      await load(); onChanged?.();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (id) => {
    try { await api.delete(`/attachments/${id}`); toast.success("Attachment removed"); await load(); onChanged?.(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white shadow-md" data-testid="attachments-dialog">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="font-display text-base font-bold text-slate-900">Receipts — {title}</h3>
          <button data-testid="close-attachments-dialog" onClick={onClose} className="text-slate-400"><X size={18} /></button>
        </div>
        <div className="p-5">
          {canWrite && (
            <label data-testid="attachment-dropzone"
              className="flex cursor-pointer flex-col items-center gap-2 rounded-md border-2 border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500 hover:border-emerald-500 hover:bg-emerald-50/40">
              <Upload size={20} />
              <span>{busy ? "Uploading…" : "Tap to photograph or choose a receipt (JPG, PNG, PDF · max 10 MB)"}</span>
              <input ref={fileRef} data-testid="attachment-file-input" type="file" multiple accept="image/*,application/pdf" capture="environment"
                className="hidden" disabled={busy} onChange={(e) => upload(Array.from(e.target.files))} />
            </label>
          )}
          {items.length ? (
            <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="attachments-list">
              {items.map((a) => (
                <li key={a.id} className="text-xs text-slate-600">
                  <Preview att={a} />
                  <p className="mt-1 truncate font-medium text-slate-800" title={a.original_filename}>{a.original_filename}</p>
                  <div className="flex items-center justify-between">
                    <span>{shortDate(a.created_at)}</span>
                    {canWrite && <button data-testid="delete-attachment-button" onClick={() => remove(a.id)} className="text-slate-400 hover:text-rose-600" aria-label="Remove"><Trash2 size={13} /></button>}
                  </div>
                </li>
              ))}
            </ul>
          ) : <p className="mt-4 text-center text-sm text-slate-400" data-testid="attachments-empty">No receipts attached yet.</p>}
        </div>
      </div>
    </div>
  );
};
